import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Ban, CheckCircle2, Plus, Minus, Trash2, RotateCcw, AlertTriangle, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function ConfirmDialog({ open, title, description, confirmLabel = "Confirm", danger = false, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className={`h-5 w-5 mt-0.5 shrink-0 ${danger ? "text-[#B74F4F]" : "text-amber-500"}`} />
          <div>
            <h3 className="font-display font-semibold text-lg">{title}</h3>
            <p className="mt-2 text-sm text-[#5C5F62] leading-relaxed">{description}</p>
          </div>
        </div>
        {danger && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-xs text-[#B74F4F] font-medium">
            ⚠ This action is irreversible and cannot be undone.
          </div>
        )}
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onCancel} className="neivai-btn-secondary text-sm">Cancel</button>
          <button
            onClick={onConfirm}
            className={`text-sm px-4 py-2 rounded-full font-medium transition ${danger ? "bg-[#B74F4F] text-white hover:bg-[#9e3e3e]" : "bg-amber-500 text-white hover:bg-amber-600"}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function daysLeft(purgeAfter) {
  if (!purgeAfter) return 0;
  const diff = new Date(purgeAfter) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function StatusBadge({ status }) {
  const colors = {
    completed: "text-[#47795E] bg-green-50",
    failed: "text-[#B74F4F] bg-red-50",
    pending: "text-amber-600 bg-amber-50",
    processing: "text-blue-600 bg-blue-50",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium uppercase tracking-wide ${colors[status] || "text-[#8A8D91] bg-gray-100"}`}>
      {status}
    </span>
  );
}

function HistoryDetailModal({ record, onClose }) {
  if (!record) return null;
  const Row = ({ label, value }) => value != null && value !== "" && (
    <div className="flex gap-3 py-2 border-b border-[#F0F0F0] last:border-0">
      <span className="w-36 shrink-0 text-xs text-[#8A8D91] font-medium pt-0.5">{label}</span>
      <span className="text-sm text-[#1A1C1E] break-all">{value}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E5E5E5]">
          <h3 className="font-display font-semibold text-lg">Render Detail</h3>
          <button onClick={onClose} className="text-[#8A8D91] hover:text-[#1A1C1E] text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-5">
          {/* Images */}
          <div className="grid grid-cols-2 gap-3">
            {record.room_image_url && (
              <div>
                <p className="text-xs text-[#8A8D91] mb-1 font-medium">Room (Input)</p>
                <img src={resolveMediaUrl(record.room_image_url)} alt="room" className="w-full rounded-lg object-cover aspect-video bg-[#F3F1EC]" />
              </div>
            )}
            {record.output_urls?.length > 0 && (
              <div>
                <p className="text-xs text-[#8A8D91] mb-1 font-medium">Output ({record.output_urls.length})</p>
                <div className="grid grid-cols-2 gap-1">
                  {record.output_urls.map((url, i) => (
                    <a key={i} href={resolveMediaUrl(url)} target="_blank" rel="noreferrer">
                      <img src={resolveMediaUrl(url)} alt={`output-${i}`} className="w-full rounded object-cover aspect-square bg-[#F3F1EC] hover:opacity-80 transition" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Info rows */}
          <div>
            <Row label="Render ID" value={record.id} />
            <Row label="Status" value={<StatusBadge status={record.status} />} />
            <Row label="Action" value={record.action} />
            <Row label="Variations" value={record.variations} />
            <Row label="Retailer ID" value={record.retailer_id} />
            <Row label="Retailer Name" value={record._retailerName} />
            <Row label="Salesperson" value={record.salesperson_name} />
            <Row label="Salesperson ID" value={record.salesperson_id} />
            <Row label="Customer Email" value={record.customer_email} />
            <Row label="Prompt" value={record.prompt} />
            <Row label="Tile Type" value={record.tile_type} />
            <Row label="Paint Surface" value={record.paint_surface} />
            <Row label="Mark Locations" value={record.mark_locations?.join(", ")} />
            <Row label="Mark Color" value={record.mark_color} />
            <Row label="Product IDs" value={record.product_ids?.join(", ")} />
            <Row label="Error" value={record.error} />
            <Row label="Created At" value={record.created_at ? new Date(record.created_at).toLocaleString() : null} />
          </div>
        </div>
      </div>
    </div>
  );
}

function HistorySection({ renders, retailers, salespersons, historySearch, setHistorySearch, historyStatusFilter, setHistoryStatusFilter, historyPage, setHistoryPage, HISTORY_PAGE_SIZE }) {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [retailerFilter, setRetailerFilter] = useState("all");
  const [salespersonFilter, setSalespersonFilter] = useState("all");
  const retailerMap = useMemo(() => Object.fromEntries(retailers.map((r) => [r.id, r.business_name || r.email])), [retailers]);

  // unique salespersons that appear in renders
  const salespersonOptions = useMemo(() => {
    const seen = new Map();
    renders.forEach((r) => { if (r.salesperson_id && r.salesperson_name) seen.set(r.salesperson_id, r.salesperson_name); });
    return [...seen.entries()];
  }, [renders]);

  const filtered = useMemo(() => {
    return renders.filter((r) => {
      const matchSearch = !historySearch ||
        (retailerMap[r.retailer_id] || "").toLowerCase().includes(historySearch.toLowerCase()) ||
        (r.retailer_id || "").toLowerCase().includes(historySearch.toLowerCase()) ||
        (r.salesperson_name || "").toLowerCase().includes(historySearch.toLowerCase()) ||
        (r.id || "").toLowerCase().includes(historySearch.toLowerCase());
      const matchStatus = historyStatusFilter === "all" || r.status === historyStatusFilter;
      const matchRetailer = retailerFilter === "all" || r.retailer_id === retailerFilter;
      const matchSalesperson = salespersonFilter === "all" || r.salesperson_id === salespersonFilter;
      return matchSearch && matchStatus && matchRetailer && matchSalesperson;
    });
  }, [renders, historySearch, historyStatusFilter, retailerFilter, salespersonFilter, retailerMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / HISTORY_PAGE_SIZE));
  const paginated = filtered.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE);

  return (
    <div className="neivai-card p-6">
      <HistoryDetailModal record={selectedRecord} onClose={() => setSelectedRecord(null)} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-2xl font-display font-medium">Generation History</h3>
          <p className="text-sm text-[#8A8D91]">{filtered.length} of {renders.length} renders</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#8A8D91]" />
            <input
              type="text"
              placeholder="Search by retailer, salesperson, ID…"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-[#E5E5E5] rounded-md w-64 focus:outline-none focus:border-[#1A1C1E]"
            />
          </div>
          <select
            value={retailerFilter}
            onChange={(e) => { setRetailerFilter(e.target.value); setHistoryPage(1); }}
            className="text-sm border border-[#E5E5E5] rounded-md px-3 py-1.5 focus:outline-none focus:border-[#1A1C1E] max-w-[180px]"
          >
            <option value="all">All Retailers</option>
            {retailers.map((r) => (
              <option key={r.id} value={r.id}>{r.business_name || r.email}</option>
            ))}
          </select>
          <select
            value={salespersonFilter}
            onChange={(e) => { setSalespersonFilter(e.target.value); setHistoryPage(1); }}
            className="text-sm border border-[#E5E5E5] rounded-md px-3 py-1.5 focus:outline-none focus:border-[#1A1C1E] max-w-[180px]"
          >
            <option value="all">All Salespersons</option>
            {salespersonOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <select
            value={historyStatusFilter}
            onChange={(e) => setHistoryStatusFilter(e.target.value)}
            className="text-sm border border-[#E5E5E5] rounded-md px-3 py-1.5 focus:outline-none focus:border-[#1A1C1E]"
          >
            <option value="all">All statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[#8A8D91] text-left border-b border-[#E5E5E5]">
            <tr>
              <th className="py-3 pr-3">Output</th>
              <th className="pr-3">Retailer</th>
              <th className="pr-3">Salesperson</th>
              <th className="pr-3">Status</th>
              <th className="pr-3">Variations</th>
              <th className="pr-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-[#8A8D91]">No records found</td></tr>
            )}
            {paginated.map((r) => (
              <tr key={r.id} onClick={() => setSelectedRecord({ ...r, _retailerName: retailerMap[r.retailer_id] })} className="border-b border-[#F0F0F0] hover:bg-[#F3F1EC] cursor-pointer transition">
                <td className="py-2 pr-3">
                  <div className="w-14 h-14 rounded overflow-hidden bg-[#F3F1EC] shrink-0">
                    {r.output_urls?.[0]
                      ? <img src={resolveMediaUrl(r.output_urls[0])} alt="render" className="h-full w-full object-cover" />
                      : <div className="h-full w-full flex items-center justify-center text-[10px] text-[#8A8D91]">—</div>}
                  </div>
                </td>
                <td className="pr-3">
                  <div>
                    <p className="font-medium">{retailerMap[r.retailer_id] || "—"}</p>
                    <p className="text-[10px] text-[#8A8D91] font-mono">{r.retailer_id}</p>
                  </div>
                </td>
                <td className="pr-3">{r.salesperson_name || <span className="text-[#8A8D91]">—</span>}</td>
                <td className="pr-3"><StatusBadge status={r.status} /></td>
                <td className="pr-3">{r.variations ?? "—"}</td>
                <td className="pr-3 text-[#8A8D91] whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-[#8A8D91]">Page {historyPage} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
              disabled={historyPage === 1}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-[#E5E5E5] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <button
              onClick={() => setHistoryPage((p) => Math.min(totalPages, p + 1))}
              disabled={historyPage === totalPages}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-[#E5E5E5] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [retailers, setRetailers] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [renders, setRenders] = useState([]);
  const [signupCredits, setSignupCredits] = useState(10);
  const [confirm, setConfirm] = useState(null); // { type, user }
  const [historySearch, setHistorySearch] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 20;

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => {
      setRetailers(r.data.retailers);
      setSalespersons(r.data.salespersons);
      setAdmins(r.data.admins);
      setDeleted(r.data.deleted || []);
    });
    api.get("/admin/history").then((r) => setRenders(r.data));
    api.get("/admin/config").then((r) => setSignupCredits(r.data.signup_credits));
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
    toast.success(`User ${!u.is_active ? "activated" : "suspended"}`);
    load();
  };

  const getRetailerName = (retailerId) =>
    retailers.find((r) => r.id === retailerId)?.business_name || "Unknown";

  const addCredits = async (u) => {
    const amount = window.prompt(`Add credits to ${u.email}:`, "50");
    if (!amount) return;
    await api.post(`/admin/users/${u.id}/credits`, { amount: Number(amount) });
    toast.success("Credits added");
    load();
  };

  const removeCredits = async (u) => {
    const amount = window.prompt(`Remove credits from ${u.email}:`, "50");
    if (!amount) return;
    await api.post(`/admin/users/${u.id}/credits`, { amount: -Number(amount) });
    toast.success("Credits removed");
    load();
  };

  const saveSignupCredits = async () => {
    await api.post("/admin/config/signup-credits", { value: Number(signupCredits) });
    toast.success("Config saved");
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    const { type, user } = confirm;
    setConfirm(null);
    try {
      if (type === "soft-delete") {
        await api.post(`/admin/users/${user.id}/soft-delete`);
        toast.success(`${user.email} moved to trash. Will be permanently deleted in 30 days.`);
      } else if (type === "hard-delete") {
        await api.delete(`/admin/users/${user.id}`);
        toast.success(`${user.email} permanently deleted.`);
      } else if (type === "restore") {
        await api.post(`/admin/users/${user.id}/restore`);
        toast.success(`${user.email} restored.`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  const UserTable = ({ users, showRetailer = false, showCredits = false }) => (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[#8A8D91] text-left border-b border-[#E5E5E5]">
          <tr>
            <th className="py-3">Email</th>
            <th>Name</th>
            {showRetailer && <th>Retailer</th>}
            {showCredits && <th>Credits</th>}
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-[#F0F0F0]">
              <td className="py-3">{u.email}</td>
              <td>{u.business_name}</td>
              {showRetailer && <td>{getRetailerName(u.retailer_id)}</td>}
              {showCredits && <td>{u.credits}</td>}
              <td>
                {u.is_active
                  ? <span className="text-[#47795E] text-xs uppercase tracking-widest">active</span>
                  : <span className="text-[#B74F4F] text-xs uppercase tracking-widest">suspended</span>}
              </td>
              <td className="text-right space-x-1.5 sm:space-x-2 whitespace-nowrap">
                {showCredits && (
                  <>
                    <button onClick={() => addCredits(u)} title="Add credits" className="inline-flex items-center gap-1 text-xs px-2 sm:px-3 py-1.5 rounded border border-[#E5E5E5] hover:border-[#1A1C1E]">
                      <Plus className="h-3 w-3" /> <span className="hidden sm:inline">Add</span>
                    </button>
                    <button onClick={() => removeCredits(u)} title="Remove credits" className="inline-flex items-center gap-1 text-xs px-2 sm:px-3 py-1.5 rounded border border-[#E5E5E5] hover:border-[#1A1C1E]">
                      <Minus className="h-3 w-3" /> <span className="hidden sm:inline">Remove</span>
                    </button>
                  </>
                )}
                <button
                  onClick={() => toggleActive(u)}
                  title={u.is_active ? "Suspend" : "Activate"}
                  className={`inline-flex items-center gap-1 text-xs px-2 sm:px-3 py-1.5 rounded border ${u.is_active ? "border-[#B74F4F] text-[#B74F4F]" : "border-[#47795E] text-[#47795E]"}`}
                >
                  {u.is_active ? <Ban className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />} <span className="hidden sm:inline">{u.is_active ? "Suspend" : "Activate"}</span>
                </button>
                <button
                  onClick={() => setConfirm({ type: "soft-delete", user: u })}
                  title="Temporary delete"
                  className="inline-flex items-center gap-1 text-xs px-2 sm:px-3 py-1.5 rounded border border-amber-400 text-amber-600 hover:bg-amber-50"
                >
                  <Trash2 className="h-3 w-3" /> <span className="hidden sm:inline">Temporary Delete</span>
                </button>
                <button
                  onClick={() => setConfirm({ type: "hard-delete", user: u })}
                  title="Permanent delete"
                  className="inline-flex items-center gap-1 text-xs px-2 sm:px-3 py-1.5 rounded border border-[#B74F4F] text-[#B74F4F] hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3" /> <span className="hidden sm:inline">Permanent Delete</span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-panel">
      <ConfirmDialog
        open={!!confirm}
        title={
          confirm?.type === "hard-delete" ? "Permanently delete user?" :
          confirm?.type === "soft-delete" ? "Temporarily delete user?" :
          "Restore user?"
        }
        description={
          confirm?.type === "hard-delete"
            ? `You are about to permanently delete ${confirm?.user?.email} (${confirm?.user?.role}). The user record and all associated data — products, renders, sessions, and payments — will be removed from the database immediately.`
            : confirm?.type === "soft-delete"
            ? `${confirm?.user?.email} will be immediately hidden from all dashboards and lists, and will be unable to log in. The account will remain in the database for 30 days. You can restore it anytime during this period. After 30 days it will be automatically and permanently deleted.`
            : `Restore ${confirm?.user?.email}? Their account will become active again and they will be able to log in.`
        }
        confirmLabel={
          confirm?.type === "hard-delete" ? "Yes, delete permanently" :
          confirm?.type === "soft-delete" ? "Yes, temporarily delete" :
          "Restore account"
        }
        danger={confirm?.type === "hard-delete"}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      />

      <header className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-6">
            <Logo />
            <span className="text-xs uppercase tracking-widest text-[#8A8D91]">Admin Panel</span>
          </div>
          <button onClick={() => { logout(); nav("/login"); }} className="text-sm inline-flex items-center gap-2 text-[#5C5F62] hover:text-[#B74F4F]">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-10 space-y-8 sm:space-y-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[
            { label: "Retailers", value: stats?.total_users ?? "…" },
            { label: "Active", value: stats?.active_users ?? "…" },
            { label: "Products", value: stats?.total_products ?? "…" },
            { label: "Renders", value: stats?.total_renders ?? "…" },
            { label: "Revenue", value: stats ? `₹${stats.revenue_inr.toLocaleString()}` : "…" },
            { label: "Credits used", value: stats?.credit_usage ?? "…" },
          ].map((s) => (
            <div key={s.label} className="neivai-card p-4">
              <p className="overline text-xs">{s.label}</p>
              <p className="mt-2 text-2xl font-display font-semibold">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Signup credit config */}
        <div className="neivai-card p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-display font-medium">Free signup credits</h3>
            <p className="text-sm text-[#8A8D91]">Credits automatically granted to new retailers on signup.</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={signupCredits}
              onChange={(e) => setSignupCredits(e.target.value)}
              className="w-24 bg-white border border-[#E5E5E5] rounded-md px-3 py-2"
            />
            <button onClick={saveSignupCredits} className="neivai-btn-primary">Save</button>
          </div>
        </div>

        {/* Retailers */}
        <div className="neivai-card p-6">
          <h3 className="text-2xl font-display font-medium">Retailers</h3>
          <UserTable users={retailers} showCredits />
        </div>

        {/* Salespersons */}
        <div className="neivai-card p-6">
          <h3 className="text-2xl font-display font-medium">Salespersons</h3>
          <UserTable users={salespersons} showRetailer />
        </div>

        {/* Admins */}
        <div className="neivai-card p-6">
          <h3 className="text-2xl font-display font-medium">Admins</h3>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[#8A8D91] text-left border-b border-[#E5E5E5]">
                <tr>
                  <th className="py-3">Email</th>
                  <th>Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((u) => (
                  <tr key={u.id} className="border-b border-[#F0F0F0]">
                    <td className="py-3">{u.email}</td>
                    <td>{u.business_name}</td>
                    <td>{u.is_active ? <span className="text-[#47795E] text-xs uppercase tracking-widest">active</span> : <span className="text-[#B74F4F] text-xs uppercase tracking-widest">suspended</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recently Deleted */}
        {deleted.length > 0 && (
          <div className="neivai-card p-6 border-amber-200 border">
            <div className="flex items-center gap-2 mb-1">
              <Trash2 className="h-5 w-5 text-amber-500" />
              <h3 className="text-2xl font-display font-medium">Recently Deleted</h3>
            </div>
            <p className="text-sm text-[#8A8D91] mb-4">These accounts are hidden and cannot log in. They will be permanently deleted when the countdown reaches 0.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[#8A8D91] text-left border-b border-[#E5E5E5]">
                  <tr>
                    <th className="py-3">Email</th>
                    <th>Role</th>
                    <th>Deleted on</th>
                    <th>Auto-deletes in</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {deleted.map((u) => (
                    <tr key={u.id} className="border-b border-[#F0F0F0]">
                      <td className="py-3">{u.email}</td>
                      <td className="capitalize">{u.role}</td>
                      <td>{u.deleted_at ? new Date(u.deleted_at).toLocaleDateString() : "—"}</td>
                      <td>
                        <span className={`font-medium ${daysLeft(u.purge_after) <= 3 ? "text-[#B74F4F]" : "text-amber-600"}`}>
                          {daysLeft(u.purge_after)} day{daysLeft(u.purge_after) !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td className="text-right space-x-1.5 sm:space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => setConfirm({ type: "restore", user: u })}
                          title="Restore"
                          className="inline-flex items-center gap-1 text-xs px-2 sm:px-3 py-1.5 rounded border border-[#47795E] text-[#47795E] hover:bg-green-50"
                        >
                          <RotateCcw className="h-3 w-3" /> <span className="hidden sm:inline">Restore</span>
                        </button>
                        <button
                          onClick={() => setConfirm({ type: "hard-delete", user: u })}
                          title="Delete now"
                          className="inline-flex items-center gap-1 text-xs px-2 sm:px-3 py-1.5 rounded border border-[#B74F4F] text-[#B74F4F] hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" /> <span className="hidden sm:inline">Delete now</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Generation History */}
        <HistorySection
          renders={renders}
          retailers={retailers}
          salespersons={salespersons}
          historySearch={historySearch}
          setHistorySearch={(v) => { setHistorySearch(v); setHistoryPage(1); }}
          historyStatusFilter={historyStatusFilter}
          setHistoryStatusFilter={(v) => { setHistoryStatusFilter(v); setHistoryPage(1); }}
          historyPage={historyPage}
          setHistoryPage={setHistoryPage}
          HISTORY_PAGE_SIZE={HISTORY_PAGE_SIZE}
        />
      </main>
    </div>
  );
}
